import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { AuthService } from '@/app/auth/auth.service';

// New (experimental) navigation. Diverges from the classic sidebar: the Core
// group is replaced by a single ungrouped "Worklist" item (no header), followed
// by the "Your site" entity group and the rest. Shares no code with the classic
// sidebar so it can be deleted cleanly once we pick a winner.
interface NavGroup {
    key: string;
    label: string | null; // null → rendered without a group header (ungrouped).
}

export interface NavItem {
    label: string;
    icon: string;
    routerLink: string;
    group: string; // matches NavGroup.key
    badgeKey?: 'validation' | 'approvals';
    permission?: string;
}

// Icons follow the design-handoff PrimeIcons mapping — every leaf item resolves
// to a unique glyph (a duplicate icon carries no recognition value).
// Every item is permission-gated (view-level). Screen-view perms are granted broadly to working
// roles in the seed; admin/config screens (Site Scope, Integrations, Adapters, Requests/Triage,
// Tag, Workflows, Access Management, AI Gateway) are gated to the roles that actually use them.
const NAV_ITEMS: readonly NavItem[] = [
    { label: 'Dashboard', icon: 'pi pi-th-large', routerLink: '/dashboard', group: 'top', permission: 'dashboard:view' },
    // Temporary label — revisit. Underlying route/component (/worklist) unchanged.
    { label: 'Work Queue', icon: 'pi pi-check-square', routerLink: '/worklist', group: 'top', permission: 'worklist:view' },
    { label: 'Quick View', icon: 'pi pi-flag', routerLink: '/triage', group: 'top', permission: 'quick-view:view' },
    { label: 'Requests', icon: 'pi pi-inbox', routerLink: '/requests', group: 'top', permission: 'request:manage' },

    { label: 'Sources', icon: 'pi pi-database', routerLink: '/sources', group: 'Clinical content', permission: 'source:view' },
    { label: 'Treatment Protocols', icon: 'pi pi-sitemap', routerLink: '/treatment-protocols', group: 'Clinical content', permission: 'protocol:view' },
    { label: 'Regimens', icon: 'pi pi-list', routerLink: '/regimens', group: 'Clinical content', permission: 'regimen:view' },
    { label: 'Order sets', icon: 'pi pi-file-edit', routerLink: '/order-sets', group: 'Clinical content', permission: 'orderset:view' },
    { label: 'Governance', icon: 'pi pi-verified', routerLink: '/governance', group: 'Clinical content', permission: 'request:manage' },

    { label: 'Site Scope', icon: 'pi pi-building', routerLink: '/site-scope', group: 'Administration', permission: 'site-scope:view' },
    { label: 'Terminology', icon: 'pi pi-book', routerLink: '/terminology', group: 'Administration', permission: 'terminology:view' },
    { label: 'Access Management', icon: 'pi pi-users', routerLink: '/settings/users-groups', group: 'Administration', permission: 'user:manage' },
    { label: 'Workflows', icon: 'pi pi-directions', routerLink: '/workflows', group: 'Administration', permission: 'workflow:manage' },
    { label: 'Journey templates', icon: 'pi pi-sitemap', routerLink: '/journey-templates', group: 'Administration', permission: 'workflow:manage' },
    { label: 'Tag management', icon: 'pi pi-tag', routerLink: '/settings/tags', group: 'Administration', permission: 'tag:manage' },
    { label: 'Integrations', icon: 'pi pi-share-alt', routerLink: '/integrations', group: 'Administration', permission: 'integration:view' },
    { label: 'Adapters', icon: 'pi pi-code', routerLink: '/adapters', group: 'Administration', permission: 'adapter:view' },
    { label: 'Audit Log', icon: 'pi pi-history', routerLink: '/audit-log', group: 'Administration', permission: 'audit:view' },
    { label: 'AI Gateway', icon: 'pi pi-microchip-ai', routerLink: '/settings/ai-gateway', group: 'Administration', permission: 'ai-gateway:view' }
] as const;

const NAV_GROUPS: readonly NavGroup[] = [
    { key: 'top', label: null },
    { key: 'Clinical content', label: 'Clinical content' },
    { key: 'Administration', label: 'Administration' }
] as const;

@Component({
    selector: 'app-sidebar-new',
    standalone: true,
    imports: [CommonModule, RouterModule, BadgeModule, RippleModule],
    templateUrl: './app-sidebar-new.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        /* Read-only site context (handoff §7): plain overline + value, no card
           the [data-site-switcher] hook makes it easy to wire a dropdown later.
           The menu itself uses the shared/classic layout styling (no overrides). */
        .site-context {
            margin: 14px 14px 12px;
            padding: 9px 12px;
            background: var(--surface-ground);
            border: 1px solid var(--surface-border);
            border-radius: var(--border-radius, 8px);
        }
        .site-context-label {
            font-size: 10.5px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--text-color-secondary);
        }
        .site-context-name {
            margin-top: 2px;
            font-size: 13px;
            font-weight: 400;
            color: var(--text-color);
        }
    `
})
export class AppSidebarNewComponent {
    private readonly auth = inject(AuthService);

    /** Current tenant's site name; falls back to "Your site" when unavailable. */
    readonly siteName = computed(() => this.auth.currentUser()?.tenantName?.trim() || 'Your site');

    readonly validationBadge = input<number | null | undefined>(undefined);
    readonly approvalsBadge = input<number | null | undefined>(undefined);

    readonly groups = NAV_GROUPS;

    readonly itemsByGroup = computed(() => {
        const out = new Map<string, NavItem[]>();
        for (const group of NAV_GROUPS) out.set(group.key, []);
        for (const item of NAV_ITEMS) {
            if (item.permission && !this.auth.hasPermission(item.permission)) continue;
            out.get(item.group)!.push(item);
        }
        return out;
    });

    badgeFor(item: NavItem): number | null | undefined {
        if (item.badgeKey === 'validation') return this.validationBadge();
        if (item.badgeKey === 'approvals') return this.approvalsBadge();
        return undefined;
    }
}
